import BigNumber from 'bignumber.js';
import groupBy from 'lodash/groupBy';
import maxBy from 'lodash/maxBy';
import { FiscalRegime } from '~/types/company';
import { DiscountType, DocumentBuilderLine, OtherManagementData } from '~/types/document-builder';
import { VatTotal } from '~/types/invoicing';
import { lineSchema } from '~/utils/parsing';
import { resolveVatExemption } from '~/utils/vat';

export const isManagementDataStamp = (data: OtherManagementData) => {
  return data.dataType === 'Bollo';
};

export const filterTaxableItems = (
  items: DocumentBuilderLine[],
  fiscalRegime: FiscalRegime | null | undefined,
): DocumentBuilderLine[] => {
  // See https://github.com/sibill-it/app/issues/2854 for explanation
  if (fiscalRegime === FiscalRegime.RF19) {
    return items;
  }
  return items.filter((i) => !i.managementData.some(isManagementDataStamp));
};

// Excludes lines with nature === N1 ("Escluse ex art. 15 DPR 633/1972").
// Such amounts are reimbursed by the customer (so they remain in the document total / amount to pay)
// but must NOT be included in the base on which cassa and ritenuta are computed. See TS-999.
export const filterContributionBaseItems = (
  items: DocumentBuilderLine[],
  fiscalRegime: FiscalRegime | null | undefined,
): DocumentBuilderLine[] => {
  return filterTaxableItems(items, fiscalRegime).filter(
    (i) => resolveVatExemption(i.vat.percentage, i.vat.taxExemption) !== 'N1',
  );
};

export const calculateStampTotal = (
  items: DocumentBuilderLine[],
  fiscalRegime: FiscalRegime | null | undefined,
): BigNumber => {
  // See https://github.com/sibill-it/app/issues/2854 for explanation
  if (fiscalRegime === FiscalRegime.RF19) {
    return BigNumber(0);
  }
  const stampLine = items.find((i) => i.managementData.some(isManagementDataStamp));
  return BigNumber(stampLine?.netPrice ?? 0);
};

export interface LineTotals {
  grossSubtotal: string;
  netSubtotal: string;
  discountedGross: string;
  netTotal: string;
  totalAmount: string;
  netDiscountAmount: string;
}

export const lineNetTotals = (line: DocumentBuilderLine): LineTotals => {
  const {
    discount,
    netPrice,
    quantity,
    vat: { percentage: vatPercentage },
  } = lineSchema.parse(line);

  const netSubtotal = quantity.multipliedBy(netPrice).decimalPlaces(8);
  const subtotalVat = netSubtotal.multipliedBy(vatPercentage).dividedBy(100).decimalPlaces(8);
  const grossSubtotal = netSubtotal.plus(subtotalVat).decimalPlaces(8);

  const discountRemainingFraction =
    discount.type === DiscountType.Percentage ? BigNumber(1).minus(discount.value.dividedBy(100)) : BigNumber(1);

  const discountAmount = discount.type === DiscountType.Amount ? discount.value : BigNumber(0);

  const netTotal = netSubtotal.multipliedBy(discountRemainingFraction).minus(discountAmount).decimalPlaces(8);
  const vatAmount = netTotal.multipliedBy(vatPercentage).dividedBy(100).decimalPlaces(8);

  const grossDiscountAmount = discountAmount.multipliedBy(BigNumber(1).plus(vatPercentage.dividedBy(100)));
  const discountedGross = grossSubtotal
    .multipliedBy(discountRemainingFraction)
    .minus(grossDiscountAmount)
    .decimalPlaces(8);

  const totalAmount = netTotal.plus(vatAmount).decimalPlaces(2);

  return {
    netSubtotal: netSubtotal.toFixed(),
    grossSubtotal: grossSubtotal.toFixed(),
    totalAmount: totalAmount.toFixed(),
    discountedGross: discountedGross.toFixed(),
    netTotal: netTotal.toFixed(),
    netDiscountAmount: discountAmount.toFixed(),
  };
};

export const lineGrossTotals = (line: DocumentBuilderLine): LineTotals => {
  const {
    discount,
    grossPrice,
    quantity,
    vat: { percentage: vatPercentage },
  } = lineSchema.parse(line);

  const grossSubtotal = quantity.multipliedBy(grossPrice).decimalPlaces(8);
  const subtotalVat = grossSubtotal
    .minus(grossSubtotal.dividedBy(vatPercentage.dividedBy(100).plus(1)))
    .decimalPlaces(8);

  const netSubtotal = grossSubtotal.minus(subtotalVat).decimalPlaces(8);

  const discountRemainingFraction =
    discount.type === DiscountType.Percentage ? BigNumber(1).minus(discount.value.dividedBy(100)) : BigNumber(1);

  const discountAmount = discount.type === DiscountType.Amount ? discount.value : BigNumber(0);

  const netDiscountAmount = discountAmount.dividedBy(BigNumber(1).plus(vatPercentage.dividedBy(100))).decimalPlaces(8);
  const netTotal = netSubtotal.multipliedBy(discountRemainingFraction).minus(netDiscountAmount).decimalPlaces(8);

  const discountedGross = grossSubtotal.multipliedBy(discountRemainingFraction).minus(discountAmount).decimalPlaces(8);

  const totalAmount = discountedGross.decimalPlaces(2);
  return {
    netSubtotal: netSubtotal.toFixed(),
    grossSubtotal: grossSubtotal.toFixed(),
    totalAmount: totalAmount.toFixed(),
    discountedGross: discountedGross.toFixed(),
    netTotal: netTotal.toFixed(),
    netDiscountAmount: netDiscountAmount.toFixed(),
  };
};

export const itemsNetTotal = (items: LineTotals[]): BigNumber => {
  return items.reduce((acc, line) => acc.plus(line.netTotal), BigNumber(0));
};

export const itemsDiscountedGrossTotal = (items: LineTotals[]): BigNumber => {
  return items.reduce((acc, line) => acc.plus(line.discountedGross), BigNumber(0));
};

export const calculateCompetencies = (
  items: LineTotals[],
  useGrossPrices: boolean,
  vatItemsTotals: VatTotal[],
): BigNumber => {
  if (useGrossPrices) {
    const vatTotal = vatItemsTotals.reduce((acc, v) => acc.plus(v.vatTotal.amount), BigNumber(0));
    const discountedGrossTotal = itemsDiscountedGrossTotal(items);

    return discountedGrossTotal.minus(vatTotal);
  }

  return itemsNetTotal(items);
};
export const findMaxVatLine = (taxableItems: DocumentBuilderLine[]) => {
  return maxBy(taxableItems, (l) => BigNumber(l.vat.percentage).toNumber())?.vat;
};

export const findMaxVat = (taxableItems: DocumentBuilderLine[]) => {
  const maxVatLine = findMaxVatLine(taxableItems);
  return maxVatLine ? BigNumber(maxVatLine.percentage).toFixed() : '0';
};

export const calculateVatItemsTotals = (
  items: DocumentBuilderLine[],
  currency: string,
  useGrossPrices: boolean,
): VatTotal[] => {
  if (items.length === 0) {
    return [];
  }

  const linesGroupedByVat = groupBy(
    items,
    ({ vat }) => (vat.percentage !== '0' ? vat.percentage : vat.taxExemption) + (vat.lawReference ?? ''),
  );

  const vatTotals = Object.values(linesGroupedByVat).map((lines) => {
    const { vat } = lines[0]!;

    const { netTotal, grossTotal } = lines.reduce(
      (acc, line) => {
        const t = useGrossPrices ? lineGrossTotals(line) : lineNetTotals(line);
        return {
          netTotal: acc.netTotal.plus(t.netTotal),
          grossTotal: acc.grossTotal.plus(t.discountedGross),
        };
      },
      {
        netTotal: BigNumber(0),
        grossTotal: BigNumber(0),
      },
    );

    let roundedNetTotal: BigNumber;
    let roundedVatTotal: BigNumber;

    if (useGrossPrices) {
      const roundedGrossTotal = grossTotal.decimalPlaces(2, BigNumber.ROUND_HALF_EVEN);
      roundedVatTotal = roundedGrossTotal
        .multipliedBy(vat.percentage)
        .dividedBy(BigNumber(100).plus(vat.percentage))
        .decimalPlaces(2, BigNumber.ROUND_HALF_EVEN);
      roundedNetTotal = roundedGrossTotal.minus(roundedVatTotal);
    } else {
      roundedNetTotal = netTotal.decimalPlaces(2, BigNumber.ROUND_HALF_EVEN);
      roundedVatTotal = roundedNetTotal.multipliedBy(vat.percentage).dividedBy(100).decimalPlaces(2);
    }

    const rawRounding = netTotal.decimalPlaces(2).minus(roundedNetTotal);

    return {
      count: lines.length,
      vatExemption: resolveVatExemption(vat.percentage, vat.taxExemption),
      vatPercentage: vat.percentage,
      vatTotal: {
        amount: roundedVatTotal.toFixed(),
        currency,
      },
      netTotal: { amount: roundedNetTotal.toFixed(), currency },
      rounding: rawRounding.isZero() ? undefined : rawRounding.decimalPlaces(2).toFixed(2),
      lawReference: vat.lawReference,
    };
  });
  return vatTotals;
};
